import React, { useEffect, useState, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import Header from "../../Header";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";
import {
  IconLookup,
  IconDefinition,
  findIconDefinition,
} from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import { AdminApi } from "../../../Api/AdminApi";
import SortableHeader from "../../../Components/Table/sortableHeader";
import ConfirmationModal from "../../../Components/Utils/ConfirmationModal";
import Pagination from "../../Utils/pagination";
import ProjectUserFilter, { ProjectUserFilters } from "../../../Components/Filters/ProjectUserFilter";
import { useAdmin } from "../../../Utils/AdminContext";

// Define the ProjectUser interface
interface ProjectUser {
  id: string;
  projectId: string;
  projectName: string;
  userId: string;
  userName: string;
  userRole: number;
}

library.add(faTrashCan);
const trashLookup: IconLookup = { prefix: "fas", iconName: "trash-can" };
const trashcanDefinition: IconDefinition = findIconDefinition(trashLookup);

function ListOfProjectUsers() {
  const { instance, accounts } = useMsal();
  const api = new AdminApi(accounts[0], instance);
  const navigate = useNavigate();
  
  const { isAdmin, setIsAdmin } = useAdmin();
  useEffect(() => {
    if (isAdmin === false) {
      navigate('/'); 
    }
  }, [isAdmin, navigate]);

  // Ref to track if component is mounted
  const isMounted = useRef(true);

  // State pour forcer le fetch manuel
  const [fetchCounter, setFetchCounter] = useState(0);

  // Pagination state - fixed page size of 20
  const [currentPage, setCurrentPage] = useState<number>(0);
  const pageSize = 20; // Fixed page size
  const [totalItems, setTotalItems] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Add state for project user filters
  const [projectUserFilters, setProjectUserFilters] = useState<ProjectUserFilters>({
    roles: [],
    projects: [],
    users: []
  });

  // New state for selected project users
  const [selectedProjectUserList, setSelectedProjectUserList] = useState<string[]>([]);

  // Nouvel état pour le tri backend
  const [sortField, setSortField] = useState<keyof ProjectUser | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null);

  // État pour stocker les utilisateurs de projet
  const [projectUsers, setProjectUsers] = useState<ProjectUser[]>([]);

  // Handler for selecting/deselecting project users
  const handleSelectProjectUser = (projectUserId: string, isChecked: boolean) => {
    if (isChecked) {
      // Add ID to the list if checkbox is checked
      setSelectedProjectUserList(prevList => [...prevList, projectUserId]);
    } else {
      // Remove ID from the list if checkbox is unchecked
      setSelectedProjectUserList(prevList => 
        prevList.filter(id => id !== projectUserId)
      );
    }
  };

  // Generate unique identifier for each project user
  const getProjectUserUniqueId = (projectUser: ProjectUser, index: number) => {
    // Si l'id existe et est unique, l'utiliser, sinon créer un identifiant unique
    return projectUser.id || `${projectUser.projectId}-${projectUser.userId}-${index}`;
  };

  // Helper function to convert userRole number to a readable string
  const getRoleName = (roleId: number): string => {
    switch (roleId) {
      case 1: return "Encoder";
      case 2: return "Validator";
      case 3: return "Project Verificator";
      case 4: return "Project Approver";
      default: return `Role ${roleId}`;
    }
  };

  // Handle API response structure
  const fetchProjectUsers = async () => {
    if (!isMounted.current) return;
    
    setIsLoading(true);
    try {
      // Get data from API - using fixed page size of 20 and applying filters and sorting
      const response = await api.getRoles(
        currentPage, 
        pageSize, 
        projectUserFilters.roles, 
        projectUserFilters.projects, 
        projectUserFilters.users,
        sortField || undefined,
        sortDirection || undefined
      );
      
      if (!isMounted.current) return;
      
      // Check if response has expected structure (for paginated API)
      if (response && response.items && Array.isArray(response.items)) {
        // If we have a paginated response
        setProjectUsers(response.items);
        setTotalItems(response.totalCount || 0);
        setTotalPages(response.totalPages || Math.ceil((response.totalCount || 0) / pageSize));
      } else if (Array.isArray(response)) {
        // If API returns an array directly (old format)
        setProjectUsers(response);
        setTotalItems(response.length);
        setTotalPages(Math.ceil(response.length / pageSize));
      } else {
        // Fallback for unexpected formats
        console.error("Unexpected API response format:", response);
        setProjectUsers([]);
        setTotalItems(0);
        setTotalPages(1);
      }
    } catch (error) {
      if (isMounted.current) {
        console.error("Error fetching project users:", error);
        setProjectUsers([]);
        setTotalItems(0);
        setTotalPages(1);
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  };

  // Load project users when fetchCounter changes - centralise control via this counter
  useEffect(() => {
    fetchProjectUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCounter]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Handler for page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Force fetch with a counter
    setFetchCounter(prev => prev + 1);
  };

  // Handler for filter changes
  const handleFiltersChange = (filters: ProjectUserFilters) => {
    // Reset to first page when filters change
    setCurrentPage(0);
    // Update filters state
    setProjectUserFilters(filters);
    // Force fetch with a counter
    setFetchCounter(prev => prev + 1);
  };

  // Gestionnaire de tri
  const sortData = (field: keyof ProjectUser) => {
    // Si on clique sur le même champ
    if (sortField === field) {
      // Cycle: asc -> desc -> null
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortField(null);
        setSortDirection(null);
      }
    } else {
      // Nouveau champ de tri: commencer par asc
      setSortField(field);
      setSortDirection('asc');
    }
    
    // Reset to first page when sorting changes
    setCurrentPage(0);
    
    // Forcer le fetch avec un compteur - APRÈS avoir mis à jour les états
    setFetchCounter(prev => prev + 1);
  };

  // Fonction pour obtenir l'indicateur de tri
  const getSortIndicator = (field: keyof ProjectUser) => {
    if (sortField === field) {
      return sortDirection === 'asc' ? ' ↑' : ' ↓';
    }
    return '';
  };

  const handleDeleteProjectUser = async (projectUser: ProjectUser) => {
    setProjectUserToDelete(projectUser);
    setIsModalOpen(true);
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteSelectedModalOpen, setIsDeleteSelectedModalOpen] = useState(false);
  const [projectUserToDelete, setProjectUserToDelete] = useState<ProjectUser | null>(null);

  const confirmDelete = async () => {
    if (projectUserToDelete) {
      await api.deleteUserProject(projectUserToDelete.projectId, projectUserToDelete.userId);
      setIsModalOpen(false);
      // Force fetch with a counter
      setFetchCounter(prev => prev + 1);
    }
  };

  const handleExportRolesCsv = async () => {
    try {
      setIsLoading(true); 
  
      await api.exportRolesCsv(
        projectUserFilters.projects,
        projectUserFilters.users,
        projectUserFilters.roles,
        sortField || undefined,
        sortDirection || undefined
      );
  
    } catch (error) {
      console.error("Error exporting project users to CSV:", error);
    } finally {
      setIsLoading(false); 
    }
  };

  // Handler for deleting selected project users
  const handleDeleteSelectedProjectUsers = async () => {
    if (selectedProjectUserList.length === 0) return;
    
    setIsDeleteSelectedModalOpen(true);
  };

  const confirmDeleteSelected = async () => {
    try {
      setIsLoading(true);
      
      // Find the selected project users objects using the unique IDs
      const selectedProjectUsers = projectUsers.filter((projectUser, index) => {
        const uniqueId = getProjectUserUniqueId(projectUser, index);
        return selectedProjectUserList.includes(uniqueId);
      });
      
      // Delete each selected project user
      for (const projectUser of selectedProjectUsers) {
        await api.deleteUserProject(projectUser.projectId, projectUser.userId);
      }
      
      // Clear the selection
      setSelectedProjectUserList([]);
      
      // Refresh the data
      setFetchCounter(prev => prev + 1);
      
    } catch (error) {
      console.error("Error deleting selected project users:", error);
    } finally {
      setIsLoading(false);
      setIsDeleteSelectedModalOpen(false);
    }
  };

  return (
    <>
      <Header projectName="" loaded={false} isAdmin={true} />
      <div className="project-container">
        <div className="button-holder">
          <input
            className="big-button mx-auto mt-3"
            type="submit"
            value="Export CSV"
            onClick={handleExportRolesCsv}
          />
          {selectedProjectUserList.length > 0 && (
            <input
  className="big-button mx-auto mt-3 ms-3"
  type="submit"
  value={`Delete Selected User Access (${selectedProjectUserList.length})`}
  onClick={handleDeleteSelectedProjectUsers}
  style={{ width: 'auto' }}
/>
          )}
        </div>
        <div className="feedstock-container">
          <h1 className="page-title">Project Users</h1>
          <ProjectUserFilter onFiltersChange={handleFiltersChange} />
          
          {isLoading ? (
            <div className="text-center my-4">Loading...</div>
          ) : (
            <>
              <table className="table mt-5">
                <thead>
                  <tr>
                    <SortableHeader<ProjectUser>
                      label="Select"
                      sortKey="id"
                      sortData={sortData}
                      getSortIndicator={getSortIndicator}
                      isAction={true}
                    />
                    <SortableHeader<ProjectUser>
                      label="Role"
                      sortKey="userRole"
                      sortData={sortData}
                      getSortIndicator={getSortIndicator}
                    />
                    <SortableHeader<ProjectUser>
                      label="Project"
                      sortKey="projectName"
                      sortData={sortData}
                      getSortIndicator={getSortIndicator}
                    />
                    <SortableHeader<ProjectUser>
                      label="User"
                      sortKey="userName"
                      sortData={sortData}
                      getSortIndicator={getSortIndicator}
                    />
                    {/* <SortableHeader<ProjectUser>
                      label="User ID"
                      sortKey="userId"
                      sortData={sortData}
                      getSortIndicator={getSortIndicator}
                    /> */}
                    <SortableHeader<ProjectUser>
                      label="Actions"
                      sortKey="id"
                      sortData={sortData}
                      getSortIndicator={getSortIndicator}
                      isAction={true}
                    />
                  </tr>
                </thead>
                <tbody>
                  {projectUsers.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center">No project users found</td>
                    </tr>
                  ) : (
                    projectUsers.map((projectUser, index) => {
                      const uniqueId = getProjectUserUniqueId(projectUser, index);
                      return (
                        <tr key={uniqueId}>
                          <td>
                            <input 
                              type="checkbox" 
                              checked={selectedProjectUserList.includes(uniqueId)}
                              onChange={(e) => handleSelectProjectUser(uniqueId, e.target.checked)}
                            />
                          </td>
                          <td>
                            {getRoleName(projectUser.userRole)}
                          </td>
                          <td>{projectUser.projectName}</td>
                          <td>{projectUser.userName}</td>
                          {/* <td>{projectUser.userId}</td> */}
                          <td>
                            <a
                              className="action-button delete-button"
                              onClick={() => handleDeleteProjectUser(projectUser)}
                            >
                              <FontAwesomeIcon icon={trashcanDefinition} />
                              <span>Delete</span>
                            </a>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
              
              <div className="pagination-wrapper">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            </>
          )}
        </div>
        
        
        <ConfirmationModal
          isOpen={isModalOpen}
          onRequestClose={() => setIsModalOpen(false)}
          onConfirm={confirmDelete}
          title="Confirm Deletion"
          message="Are you sure you want to delete this project user assignment? This action cannot be undone."
          confirmButtonText="Delete"
          cancelButtonText="Cancel"
          requireComment={false}
        />
        
        <ConfirmationModal
          isOpen={isDeleteSelectedModalOpen}
          onRequestClose={() => setIsDeleteSelectedModalOpen(false)}
          onConfirm={confirmDeleteSelected}
          title="Confirm Multiple Deletion"
          message={`Are you sure you want to delete ${selectedProjectUserList.length} project user assignment(s)? This action cannot be undone.`}
          confirmButtonText="Delete All"
          cancelButtonText="Cancel"
          requireComment={false}
        />
      </div>
    </>
  );
}

export default ListOfProjectUsers;